import { z } from 'zod';
import { MoneySchema } from './common.js';
// Decimal columns serialize either as a canonical string or as a number —
// match the convention used in vat.ts / wht.ts.
const DecimalLike = z.union([MoneySchema, z.coerce.number()]);
// Variance can be negative (GL < filed) so allow signed numbers.
const SignedDecimalLike = z.union([z.string(), z.coerce.number()]);
// ============================================================
// Wire enums — mirror Prisma TaxReconForm / TaxReconStatus.
// PHO_PHO_30 keeps the prisma-safe ASCII alias (Prisma enum members can't
// start with a digit and can't contain a dot, so '.' becomes '_PHO_').
// ============================================================
export const TaxReconFormSchema = z.enum(['PND1', 'PND3', 'PND53', 'PND54', 'PHO_PHO_30']);
export const TaxReconStatusSchema = z.enum(['OPEN', 'EXPLAINED', 'RESOLVED']);
// Variance severity — derived field, not persisted. UI uses it to colour-code
// rows. Threshold rule lives in tax-recon.service.ts (5% OR 1,000 THB).
export const TaxReconSeveritySchema = z.enum(['OK', 'WARNING', 'CRITICAL']);
// ============================================================
// TaxReconRow — persisted snapshot
// ============================================================
export const TaxReconRowSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    period: z.string().regex(/^\d{6}$/),
    taxForm: TaxReconFormSchema,
    filedAmount: DecimalLike,
    glAmount: DecimalLike,
    varianceAmount: SignedDecimalLike,
    variancePercent: SignedDecimalLike,
    status: TaxReconStatusSchema,
    explanation: z.string().nullable(),
    resolvedBy: z.string().nullable(),
    resolvedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    // Derived (server-computed) — convenience for the UI.
    severity: TaxReconSeveritySchema,
    // True when the upstream Wht/Vat filing table was missing (cross-module
    // not yet merged) — UI shows a "filing data unavailable" hint.
    filingDataAvailable: z.boolean(),
});
// ============================================================
// GET /tax-recon?period=YYYYMM — one row per form for the requested period.
// ============================================================
export const TaxReconSnapshotResponseSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
    rows: z.array(TaxReconRowSchema),
    totals: z.object({
        filed: DecimalLike,
        gl: DecimalLike,
        // signed
        variance: SignedDecimalLike,
        // Counts
        open: z.number().int().nonnegative(),
        explained: z.number().int().nonnegative(),
        resolved: z.number().int().nonnegative(),
    }),
});
// ============================================================
// POST /tax-recon/refresh — body
// ============================================================
export const RefreshTaxReconSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
});
// ============================================================
// PATCH /tax-recon/:id — body
// ============================================================
export const UpdateTaxReconSchema = z.object({
    status: TaxReconStatusSchema.optional(),
    explanation: z.string().nullable().optional(),
});
// ============================================================
// GET /tax-recon/summary?year=YYYY — 12-month grid
// One cell = (period, form) with just the numbers needed for the heatmap.
// ============================================================
export const TaxReconSummaryCellSchema = z.object({
    period: z.string().regex(/^\d{6}$/),
    taxForm: TaxReconFormSchema,
    filedAmount: DecimalLike,
    glAmount: DecimalLike,
    varianceAmount: SignedDecimalLike,
    status: TaxReconStatusSchema,
    severity: TaxReconSeveritySchema,
});
export const TaxReconSummaryResponseSchema = z.object({
    year: z.number().int(),
    cells: z.array(TaxReconSummaryCellSchema),
});
//# sourceMappingURL=tax-recon.js.map